import React, { useState } from 'react';
import { Avatar, Box, Button, Stack, Typography } from '@mui/material';
import palette from '../assets/sass/palette.scss';
import { format } from 'date-fns';
import { useAuth } from '../contexts/AuthContext';
import { doc, updateDoc } from 'firebase/firestore';
import { useFirebase } from '../contexts/FirebaseContext';
import { useProtectedAction } from '../hooks/useProtectedAction';
import { useApi } from '../services/api';

const GameSingleDateActions = ({ gameData, onAvailable, onDecline, usersWithPhotoURL }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { currentUser } = useAuth();
  const { db } = useFirebase();
  const { post } = useApi();

  // Check if user has already accepted
  const hasUserAccepted = () => {
    if (!currentUser) return false;
    return gameData?.players?.some((player) => player.user === currentUser.uid);
  };

  // Check if user has declined
  const hasUserDeclined = () => {
    if (!currentUser) return false;
    const declinedBy = gameData.declinedBy || [];
    return declinedBy.includes(currentUser.uid);
  };

  const outputSelectedDate = (date) => {
    if (date && date.toDate) {
      return format(date.toDate(), 'EEEE d MMM');
    }
    if (date && date.seconds) {
      return format(new Date(date.seconds * 1000), 'EEEE d MMM');
    }
    if (date instanceof Date) {
      return format(date, 'EEEE d MMM');
    }
    return 'TBD';
  };

  const outputSelectedTime = (date) => {
    if (date && date.toDate) {
      return format(date.toDate(), 'h:mmaaa');
    }
    if (date && date.seconds) {
      return format(new Date(date.seconds * 1000), 'h:mmaaa');
    }
    if (date instanceof Date) {
      return format(date, 'h:mmaaa');
    }
    return 'TBD';
  };

  const getUserPhotoURL = (uid) => {
    const user = usersWithPhotoURL?.find((user) => user?.uid === uid || user?.user === uid);
    return user ? user?.photoURL : null;
  };

  const getUserInitial = (uid) => {
    const user = usersWithPhotoURL?.find((user) => user?.uid === uid || user?.user === uid);
    return user ? user?.initial : '';
  };

  const getAcceptedPlayers = () => {
    return gameData?.players || [];
  };

  const handleImAvailableAction = async () => {
    if (isSubmitting) return;

    setIsSubmitting(true);
    try {
      // For single-date games, we need to manually manage the players array
      const gameRef = doc(db, 'games', gameData.id);

      // Add user to players array if not already there
      const currentPlayers = gameData.players || [];
      const isAlreadyPlayer = currentPlayers.some((player) => player.user === currentUser.uid);

      if (!isAlreadyPlayer) {
        const newPlayer = {
          user: currentUser.uid,
          userName: currentUser.displayName || currentUser.email || '',
          score: 0,
          paid: false,
        };

        await updateDoc(gameRef, {
          players: [...currentPlayers, newPlayer],
          declinedBy: (gameData.declinedBy || []).filter((uid) => uid !== currentUser.uid),
        });
      } else {
        // Just remove from declined if already a player
        await updateDoc(gameRef, {
          declinedBy: (gameData.declinedBy || []).filter((uid) => uid !== currentUser.uid),
        });
      }

      if (onAvailable) {
        onAvailable();
      }
    } catch (error) {
      console.error('Error accepting invitation:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeclineEventAction = async () => {
    if (isSubmitting) return;

    setIsSubmitting(true);
    try {
      // Add the current user to the declinedBy array on the game document
      const gameRef = doc(db, 'games', gameData.id);
      await updateDoc(gameRef, {
        declinedBy: [...(gameData.declinedBy || []), currentUser.uid],
      });

      // Remove user from players if they were accepted
      if (hasUserAccepted()) {
        const updatedPlayers = gameData.players.filter((player) => player.user !== currentUser.uid);
        await updateDoc(gameRef, {
          players: updatedPlayers,
        });
      }

      // Send WhatsApp notification
      try {
        await post('/game/send-decline-notification', {
          gameId: gameData.id,
          userId: currentUser.uid,
        });
      } catch (whatsappError) {
        console.warn('Error sending WhatsApp decline notification:', whatsappError);
        // Don't fail the decline action if WhatsApp notification fails
      }

      if (onDecline) {
        onDecline();
      }
    } catch (error) {
      console.error('Error declining event:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Protected action handlers for unauthenticated users
  const [handleImAvailable] = useProtectedAction(handleImAvailableAction, {
    message: 'Please sign in to join this game.',
  });

  const [handleDeclineEvent] = useProtectedAction(handleDeclineEventAction, {
    message: 'Please sign in to decline this game.',
  });

  // Check if user is invited (in players, declined, or invitations array)
  const isUserInvited = () => {
    if (!currentUser) return false;

    // Check if user is a player
    if (gameData?.players?.some((player) => player.user === currentUser.uid)) {
      return true;
    }

    // Check if user has declined
    if ((gameData.declinedBy || []).includes(currentUser.uid)) {
      return true;
    }

    // Check if user is in invitations array
    if (gameData?.invitations?.some((inv) => inv.user === currentUser.uid)) {
      return true;
    }

    return false;
  };

  // Check if game is finalized/completed (hide action buttons)
  const isGameFinalized = () => {
    return gameData.status === 'finalised' || gameData.status === 'completed';
  };

  const userAccepted = hasUserAccepted();
  const userDeclined = hasUserDeclined();
  const acceptedPlayers = getAcceptedPlayers();
  const userInvited = isUserInvited();
  const gameFinalized = isGameFinalized();

  // Debug logging
  console.log('GameSingleDateActions Debug:', {
    currentUser: currentUser?.uid,
    userAccepted,
    userDeclined,
    gameDataPlayers: gameData?.players,
    acceptedPlayers,
    gameDataDeclinedBy: gameData?.declinedBy,
  });

  return (
    <Stack spacing={3} sx={{ paddingTop: 3, width: '100%', alignItems: 'center' }}>
      {/* Single Date Display */}
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={3}
        sx={{ width: '100%', alignItems: 'stretch', justifyContent: 'center' }}
      >
        <Box
          sx={{
            backgroundColor: userAccepted ? palette.lime : palette.white,
            borderRadius: '24px',
            padding: 2,
            width: '100%',
            maxWidth: { xs: '100%', sm: '350px' }, // Full width on mobile
            flexGrow: 0,
          }}
        >
          <Typography variant="moduleHeader">{outputSelectedDate(gameData.date)}</Typography>
          <Typography variant="small" sx={{ mt: 1 }}>
            {outputSelectedTime(gameData.date)}
          </Typography>

          {/* Show accepted players */}
          <Stack direction="row" sx={{ mt: 1 }}>
            {acceptedPlayers.map((player, index) => {
              const initial = getUserInitial(player.user);
              const photoURL = getUserPhotoURL(player.user);

              if (photoURL) {
                return (
                  <Box
                    key={index}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      marginLeft: index > 0 ? -1 : 0,
                    }}
                  >
                    <Avatar
                      alt={player.userName || player.user}
                      sx={{ bgcolor: index % 2 === 0 ? '#E9FFEA' : '#F9EDFF' }}
                      src={photoURL}
                    />
                  </Box>
                );
              }
              return (
                <Box
                  key={index}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    marginLeft: index > 0 ? -1 : 0,
                  }}
                >
                  <Avatar
                    alt={player.userName || player.user}
                    sx={{ bgcolor: index % 2 === 0 ? '#E9FFEA' : '#F9EDFF' }}
                  >
                    <Typography
                      variant="factorA"
                      color="primary"
                      component="small"
                      sx={{ color: '#4C4C4C' }}
                    >
                      {initial}
                    </Typography>
                  </Avatar>
                </Box>
              );
            })}
            {!acceptedPlayers.length && <Avatar sx={{ opacity: 0 }} />}
          </Stack>

          <Stack
            direction="row"
            sx={{ justifyContent: 'space-between', alignItems: 'flex-end', mt: 1 }}
          >
            <Typography>
              {acceptedPlayers.length} {acceptedPlayers.length === 1 ? 'Player' : 'Players'}{' '}
              available
            </Typography>
          </Stack>
        </Box>
      </Stack>

      {/* Action Buttons - conditionally show based on invitation status and game finalization */}
      {!gameFinalized && (
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          sx={{ width: '100%', maxWidth: '700px', mt: 2, justifyContent: 'center' }}
        >
          {/* I'm Available button - always show for invited users */}
          {userInvited && (
            <Button
              variant="contained"
              onClick={handleImAvailable}
              disabled={isSubmitting || userAccepted} // Disable if already accepted
              sx={{
                flex: 1,
                maxWidth: { xs: '100%', sm: '250px' }, // Full width on mobile, max 250px on desktop
                backgroundColor: userAccepted
                  ? '#E3DCE7 !important'
                  : 'rgb(171, 232, 50) !important', // Gray if accepted, green if not
                color: userAccepted ? 'rgba(0, 0, 0, 0.4) !important' : 'black !important', // Dimmed text if accepted
                fontWeight: 600,
                height: '48px',
                border: 'none !important',
                boxShadow: 'none !important',
                '&:hover': {
                  backgroundColor: userAccepted
                    ? '#E3DCE7 !important'
                    : 'rgb(150, 210, 40) !important', // No hover effect if accepted
                  color: userAccepted ? 'rgba(0, 0, 0, 0.4) !important' : 'black !important',
                  boxShadow: 'none !important',
                },
                '&:disabled': {
                  backgroundColor: '#E3DCE7 !important',
                  color: 'rgba(0, 0, 0, 0.4) !important', // Dimmed black text when disabled
                  boxShadow: 'none !important',
                },
                '&.Mui-disabled': {
                  backgroundColor: '#E3DCE7 !important',
                  color: 'rgba(0, 0, 0, 0.4) !important',
                  boxShadow: 'none !important',
                },
              }}
            >
              {isSubmitting ? 'Submitting...' : userAccepted ? '✓ Available' : "🏌️‍♂️ I'm Available"}
            </Button>
          )}

          {/* Decline button - only show if user is invited */}
          {userInvited && (
            <Button
              variant="contained"
              onClick={handleDeclineEvent}
              disabled={isSubmitting || userDeclined}
              sx={{
                flex: 1,
                maxWidth: { xs: '100%', sm: '250px' }, // Full width on mobile, max 250px on desktop
                backgroundColor: userDeclined ? '#F8F8F8 !important' : 'white !important',
                color: userDeclined ? 'rgba(0, 0, 0, 0.4) !important' : 'black !important',
                fontWeight: 600,
                height: '48px',
                border: '1px solid #E3DCE7 !important',
                boxShadow: 'none !important',
                '&:hover': {
                  backgroundColor: userDeclined ? '#F8F8F8 !important' : '#F5F5F5 !important',
                  border: '1px solid #E3DCE7 !important',
                  boxShadow: 'none !important',
                },
                '&:disabled': {
                  backgroundColor: '#F8F8F8 !important',
                  color: 'rgba(0, 0, 0, 0.4) !important',
                  border: '1px solid #E3DCE7 !important',
                  boxShadow: 'none !important',
                },
                '&.Mui-disabled': {
                  backgroundColor: '#F8F8F8 !important',
                  color: 'rgba(0, 0, 0, 0.4) !important',
                  border: '1px solid #E3DCE7 !important',
                  boxShadow: 'none !important',
                },
              }}
            >
              {userDeclined ? '🚫 Declined' : '🚫 Decline Event'}
            </Button>
          )}
        </Stack>
      )}
    </Stack>
  );
};

export default GameSingleDateActions;
